import { useCallback, useEffect, useState } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabaseClient';
import { GBP_ENABLED } from '../lib/featureFlags';
import { useBusinessTableChanges } from './useBusinessTableChanges';

const GBP_LIVE_TABLES = ['google_business_post_drafts', 'google_business_history'] as const;

export interface UpcomingGbpPostSnippet {
  id: string;
  content: string;
  scheduled_for: string | null;
  source: 'ai' | 'manual' | string | null;
  has_image: boolean;
  media_id: string | null;
  image_url: string | null;
  created_at: string;
  connection_id: string | null;
  /** pending = staged draft; published = live post from history. */
  status: 'pending' | 'published';
}

type DraftRow = {
  id: string;
  content: string;
  scheduled_for: string | null;
  source: string | null;
  has_image: boolean | null;
  media_id: string | null;
  created_at: string;
  connection_id: string | null;
  media_library?: { url: string } | { url: string }[] | null;
};

function mediaUrlFromJoin(
  rel: DraftRow['media_library'],
): string | null {
  if (!rel) return null;
  if (Array.isArray(rel)) return rel[0]?.url || null;
  return rel.url || null;
}

type HistoryRow = {
  id: string;
  content: string | null;
  created_at: string;
  connection_id: string | null;
  metadata: Record<string, unknown> | null;
};

function mapHistoryRow(r: HistoryRow): UpcomingGbpPostSnippet {
  const meta = r.metadata && typeof r.metadata === 'object' ? r.metadata : {};
  const mediaUrl = typeof meta.media_url === 'string' && meta.media_url ? meta.media_url : null;
  const mediaId = typeof meta.media_id === 'string' && meta.media_id ? meta.media_id : null;
  const sourceRaw = typeof meta.source === 'string' ? meta.source : null;
  const source = sourceRaw === 'manual' ? 'manual' : sourceRaw ? 'ai' : null;

  return {
    id: r.id,
    content: String(r.content || ''),
    scheduled_for: r.created_at,
    source,
    has_image: !!(meta.has_image || mediaUrl),
    media_id: mediaId,
    image_url: mediaUrl,
    created_at: r.created_at,
    connection_id: r.connection_id,
    status: 'published',
  };
}

type UseUpcomingGbpPostsOptions = {
  /** Max pending drafts to return (default 20). Use a higher value for calendars. */
  limit?: number;
  /**
   * When true, also include published posts from history (green “completed” on the calendar).
   * Pending-only surfaces (upcoming queue) should leave this false.
   */
  includePublished?: boolean;
  /** How far back to load published history when includePublished (default 60 days). */
  publishedLookbackDays?: number;
};

/**
 * Pending Google Business posts for the Control Centre Upcoming Media panel
 * and the Marketing calendar. Returns staged/scheduled drafts only
 * (status = pending), with image URLs when present.
 */
export function useUpcomingGbpPostsSnippet(options: UseUpcomingGbpPostsOptions = {}) {
  const { limit = 20, includePublished = false, publishedLookbackDays = 60 } = options;
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const [posts, setPosts] = useState<UpcomingGbpPostSnippet[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (opts: { silent?: boolean } = {}) => {
    if (!GBP_ENABLED || !businessId) {
      setPosts([]);
      setLoading(false);
      return;
    }

    if (!opts.silent) setLoading(true);
    try {
      // Explicit FK hint — drafts ↔ media_library have two relationships.
      const attempts = [
        'id, content, scheduled_for, source, has_image, media_id, created_at, connection_id, media_library!google_business_post_drafts_media_id_fkey(url)',
        'id, content, scheduled_for, source, has_image, media_id, created_at, connection_id, media_library!media_id(url)',
        'id, content, scheduled_for, source, has_image, media_id, created_at, connection_id',
      ];

      let rows: DraftRow[] = [];
      let lastError: unknown = null;
      for (const select of attempts) {
        const { data, error } = await supabase
          .from('google_business_post_drafts')
          .select(select)
          .eq('business_id', businessId)
          .eq('status', 'pending')
          .order('scheduled_for', { ascending: true, nullsFirst: false })
          .limit(limit);
        if (!error) {
          rows = (data as DraftRow[]) || [];
          lastError = null;
          break;
        }
        lastError = error;
      }
      if (lastError) throw lastError;

      const missingIds = rows
        .filter((r) => r.media_id && !mediaUrlFromJoin(r.media_library))
        .map((r) => r.media_id as string);

      const urlById = new Map<string, string>();
      if (missingIds.length > 0) {
        const { data: mediaRows } = await supabase
          .from('media_library')
          .select('id, url')
          .in('id', [...new Set(missingIds)]);
        for (const m of mediaRows || []) {
          urlById.set(m.id as string, m.url as string);
        }
      }

      const pending: UpcomingGbpPostSnippet[] = rows.map((r) => {
        const joined = mediaUrlFromJoin(r.media_library);
        const imageUrl =
          joined || (r.media_id ? urlById.get(r.media_id) || null : null);
        return {
          id: r.id,
          content: r.content,
          scheduled_for: r.scheduled_for,
          source: r.source,
          has_image: !!(r.has_image || imageUrl),
          media_id: r.media_id,
          image_url: imageUrl,
          created_at: r.created_at,
          connection_id: r.connection_id,
          status: 'pending' as const,
        };
      });

      let published: UpcomingGbpPostSnippet[] = [];
      if (includePublished) {
        const since = new Date();
        since.setDate(since.getDate() - Math.max(1, publishedLookbackDays));
        const { data: histRows, error: histErr } = await supabase
          .from('google_business_history')
          .select('id, content, created_at, connection_id, metadata')
          .eq('business_id', businessId)
          .eq('event_type', 'post')
          .eq('metadata->>status', 'published')
          .gte('created_at', since.toISOString())
          .order('created_at', { ascending: false })
          .limit(Math.max(limit, 100));
        if (histErr) {
          console.warn('Failed to load published GBP posts for calendar:', histErr);
        } else {
          published = ((histRows as HistoryRow[]) || []).map(mapHistoryRow);
        }
      }

      setPosts([...pending, ...published]);
    } catch (err) {
      console.error('Failed to load upcoming GBP posts:', err);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [businessId, limit, includePublished, publishedLookbackDays]);

  const refresh = useCallback(() => load(), [load]);

  useEffect(() => {
    void load();
  }, [load]);

  // Live: refetch (without a loading flash) when posts change server-side.
  useBusinessTableChanges(businessId, GBP_LIVE_TABLES, () => void load({ silent: true }), {
    enabled: GBP_ENABLED,
  });

  return { posts, loading, refresh };
}
